<template>
  <ue-modal
    ref="modal"
    v-bind="$props"
    v-on="filteredListeners"
    @show="onShow"
    @hide="onHide"
    @closed="onClosed"
  >
    <template v-if="$slots.default">
      <slot></slot>
    </template>
    <template v-if="$slots.header" #header>
      <slot name="header"></slot>
    </template>
    <template v-if="$slots.footer" #footer>
      <slot name="footer"></slot>
    </template>
    <template v-if="$slots['close-icon']" #close-icon>
      <slot name="close-icon"></slot>
    </template>
  </ue-modal>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { useModalStore } from '@/stores/ModalStore'
import { reportError } from '@lib/errorHandler'
import UeModal from '@components/base/UeModal.vue'

/**
 * Global modal wrapper that maintains backwards compatibility with the store-based UeModal
 *
 * This component wraps the refactored UeModal and provides the global store integration
 * that was previously built into UeModal. Use this component for existing implementations
 * that rely on global modal state management through ModalStore.
 *
 * For new implementations, consider using UeModal directly with local state management.
 */
export default defineComponent({
  name: 'UeGlobalModal',
  components: {
    UeModal,
  },
  props: {
    // Modal identifier - required
    name: {
      type: String,
      required: true,
    },
    // Content
    title: {
      type: String,
      default: null,
    },
    // Show the close button in the header
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    // Additional class for the container
    containerClass: {
      type: String,
      default: '',
    },
    height: {
      type: String,
      default: 'auto',
    },
    width: {
      type: String,
      default: '600px', // default vue-js-modal width
    },
    maxWidth: {
      type: Number,
      default: Infinity,
    },
    // Basic behavior props
    adaptive: {
      type: Boolean,
      default: true,
    },
    classes: {
      type: [String, Array],
      default: '',
    },
    clickToClose: {
      type: Boolean,
      default: true,
    },
    /**
     * An optional function to override the close behavior.
     * This function receives a `close` callback as its only argument. Call `close()` to proceed with closing the modal.
     * You can perform asynchronous or synchronous logic before calling `close()` (e.g., show a confirmation dialog).
     * @param {Function} close - Call this function to close the modal.
     */
    closeOverride: {
      type: Function as PropType<(close: () => void) => void>,
    },
  },
  data() {
    return {
      isUpdatingFromStore: false,
    }
  },
  computed: {
    filteredListeners() {
      // Filter out the events we're explicitly handling to avoid duplicate handlers
      const { show: _show, hide: _hide, closed: _closed, ...otherListeners } = this.$listeners
      return otherListeners
    },
  },
  methods: {
    // Public methods that match vue-js-modal
    show(params = {}) {
      this.$refs.modal.show(params)
    },
    hide() {
      const modalStore = useModalStore()
      const closer = (): void => {
        modalStore.close(this.name)
      }

      if (this.closeOverride) {
        try {
          this.closeOverride(closer)
        } catch (e) {
          reportError(e, 'closeOverride', 'UeGlobalModal')
        }
      } else {
        closer()
      }
    },
    toggle() {
      this.$refs.modal.toggle()
    },
    // Event handlers for store integration
    onClosed() {
      // Ensure the ModalStore is updated when the modal is closed by any means
      try {
        const modalStore = useModalStore()
        modalStore.close(this.name)
      } catch (e) {
        reportError(e, 'notify', 'UeGlobalModal')
      }
    },
    onShow(params) {
      // Update the store when the modal is shown (unless we're updating from store)
      if (!this.isUpdatingFromStore) {
        try {
          const modalStore = useModalStore()
          modalStore.open(this.name, params)
        } catch (e) {
          reportError(e, 'show', 'UeGlobalModal')
        }
      }
    },
    onHide() {
      // Update the store when the modal is hidden (unless we're updating from store)
      if (!this.isUpdatingFromStore) {
        try {
          const modalStore = useModalStore()
          modalStore.close(this.name)
        } catch (e) {
          reportError(e, 'hide', 'UeGlobalModal')
        }
      }
    },
  },
  mounted() {
    // Listen for changes in the ModalStore to automatically show or hide this modal
    try {
      const modalStore = useModalStore()

      const unwatch = modalStore.$subscribe((mutation, state) => {
        // Defer the show/hide calls to avoid updating during render cycle
        this.$nextTick(() => {
          this.isUpdatingFromStore = true
          if (state.openModalNames.includes(this.name)) {
            const params = state.modalParams[this.name] || {}
            this.$refs.modal.show(params)
          } else {
            this.$refs.modal.hide()
          }
          // Reset the flag after a tick to ensure events have been processed
          this.$nextTick(() => {
            this.isUpdatingFromStore = false
          })
        })
      })

      this.$options._modalStoreUnwatch = unwatch
    } catch (e) {
      reportError(e, 'subscribe', 'UeGlobalModal')
    }
  },
  beforeDestroy() {
    // Clean up the store subscription
    if (this.$options._modalStoreUnwatch) {
      this.$options._modalStoreUnwatch()
    }
  },
})
</script>
